import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {PosterZoomModes} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/poster-zoom-options/poster-zoom-options.types';
import {updateTimelineWidthPerSecond} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {Tooltip} from '@Components/tooltip';
import {zoomTimelineIn, zoomTimelineOut} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import styles from './poster-zoom-options.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

const SCALE_VAL_TIMEOUT = 3000;

export function PosterZoomOptions(): ReactElement | null {
  const scaling = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.scaling;
  });
  const scaleValTimeout = useRef<number | undefined>();
  const showFitToScreen = !scaling?.isCurrentScaleBestFit;
  const scale = scaling?.scale ?? 1;
  const [showScaleVal, setShowScaleVal] = useState(false);
  const [currentMode, setCurrentMode] = useState(PosterZoomModes.POSTER);
  const isExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const widthPerSecondForTimeline = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const fitWidthPerSecondForTimeline = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.fitWidthPerSecond;
  });
  const timelineScale = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.timelineScale;
  });
  const dispatch = useAppDispatch();
  const zoomContainerRef = useRef<HTMLDivElement>(null);

  let width = 78;

  useEffect((): void => {
    if (!isExpanded && currentMode === PosterZoomModes.TIMELINES) {
      fitToScreen();
      setCurrentMode(PosterZoomModes.POSTER);
    }
  }, [isExpanded]);

  useEffect(() => {
    setShowScaleVal(true);
    clearTimeout(scaleValTimeout.current);

    scaleValTimeout.current = window.setTimeout(() => {
      setShowScaleVal(false);
    }, SCALE_VAL_TIMEOUT);
  }, [scale, timelineScale]);

  const isTimelineZoomEnabled = (): boolean => {
    return currentMode === PosterZoomModes.TIMELINES;
  };

  const doShowFitToScreen = (): boolean => {
    if (isTimelineZoomEnabled()) {
      return fitWidthPerSecondForTimeline !== widthPerSecondForTimeline;
    }

    return showFitToScreen;
  };

  if (showScaleVal) {
    width += 36;
  }
  if (doShowFitToScreen()) {
    width += 36;
  }

  if (showScaleVal && showFitToScreen) {
    width += 4;
  }

  if (isExpanded) {
    width += 36;
  }

  const zoomOut = (): void => {
    if (currentMode === PosterZoomModes.POSTER) {
      window.posterEditor?.whiteboard?.scaling.zoomOut();
    } else if (widthPerSecondForTimeline) {
      zoomTimelineOut(widthPerSecondForTimeline);
    }
  };

  const zoomIn = (): void => {
    if (currentMode === PosterZoomModes.POSTER) {
      window.posterEditor?.whiteboard?.scaling.zoomIn();
    } else if (widthPerSecondForTimeline) {
      zoomTimelineIn(widthPerSecondForTimeline);
    }
  };

  const fitToScreen = (): void => {
    if (currentMode === PosterZoomModes.POSTER) {
      window.posterEditor?.whiteboard?.scaling.fitToScreen();
    } else if (fitWidthPerSecondForTimeline) {
      dispatch(updateTimelineWidthPerSecond(fitWidthPerSecondForTimeline));
    }
  };

  const isPosterZoomOptionSelected = (): boolean => {
    return currentMode === PosterZoomModes.POSTER;
  };

  const doShowScaleVal = (): boolean => {
    return !(!showScaleVal || (!isPosterZoomOptionSelected() && !timelineScale));
  };

  const getScaleVal = (): number => {
    if (isPosterZoomOptionSelected()) {
      return scale * 100;
    }

    return timelineScale ?? 100;
  };

  const onSwitchMode = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (currentMode === PosterZoomModes.POSTER) {
      setCurrentMode(PosterZoomModes.TIMELINES);
    } else {
      setCurrentMode(PosterZoomModes.POSTER);
    }
  };

  const getTooltipTop = (): string | undefined => {
    if (!zoomContainerRef.current) {
      return undefined;
    }

    const buttonHeight = 32;
    const tooltipArrowHeight = 16;
    const padding = 4;
    return `${zoomContainerRef.current.getBoundingClientRect().top - buttonHeight - tooltipArrowHeight - padding}px`;
  };

  return (
    <div style={{width: `${width}px`}} className={`${styles.zoomOpts} spacing-m-r-1`}>
      <div className={`${styles.optionsContainer} ${styles.icons}`}>
        <Icon className={styles.minWidth} icon="icon-zoom-in" onClick={zoomIn} shape={IconShape.SQUARE} type={IconType.TRANSPARENT} isUsedAsButton size={IconSize.SIZE_ICON_16} />
        <Icon className={styles.minWidth} icon="icon-zoom-out" onClick={zoomOut} shape={IconShape.SQUARE} type={IconType.TRANSPARENT} isUsedAsButton size={IconSize.SIZE_ICON_16} />
        {doShowFitToScreen() ? (
          <Icon
            className={styles.minWidth}
            icon="icon-fit-to-screen"
            onClick={fitToScreen}
            shape={IconShape.SQUARE}
            type={IconType.TRANSPARENT}
            isUsedAsButton
            size={IconSize.SIZE_ICON_16}
          />
        ) : null}
        {doShowScaleVal() ? <Text val={`${Math.round(getScaleVal())}%`} size={TextSize.XXSMALL} /> : null}
        {isExpanded ? (
          <ClickableDiv ref={zoomContainerRef} onClick={onSwitchMode} className={`${styles.zoomSvgContainer} hover-transition-all flex-center radius-4`}>
            <Tooltip
              style={{
                top: getTooltipTop(),
              }}
              id="zoom-tooltip"
              text={<Text val={isPosterZoomOptionSelected() ? window.i18next.t('pmwjs_zoom_design') : window.i18next.t('pmwjs_zoom_timeline')} size={TextSize.XSMALL} />}
              className={styles.zoomTooltip}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="transparent">
              <path
                d="M13.3104 1.5H3.64376C2.90738 1.5 2.31042 2.09695 2.31042 2.83333V6.83333C2.31042 7.56971 2.90738 8.16667 3.64376 8.16667H13.3104C14.0468 8.16667 14.6438 7.56971 14.6438 6.83333V2.83333C14.6438 2.09695 14.0468 1.5 13.3104 1.5Z"
                stroke={!isPosterZoomOptionSelected() ? PMW_COLORS_NEUTRAL.NEUTRAL_18 : PMW_COLORS_NEUTRAL.NEUTRAL_70}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3104 11H3.64376C2.90738 11 2.31042 11.597 2.31042 12.3333V13.3333C2.31042 14.0697 2.90738 14.6667 3.64376 14.6667H13.3104C14.0468 14.6667 14.6438 14.0697 14.6438 13.3333V12.3333C14.6438 11.597 14.0468 11 13.3104 11Z"
                stroke={isPosterZoomOptionSelected() ? PMW_COLORS_NEUTRAL.NEUTRAL_18 : PMW_COLORS_NEUTRAL.NEUTRAL_70}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.563 11.5V14.1667"
                stroke={isPosterZoomOptionSelected() ? PMW_COLORS_NEUTRAL.NEUTRAL_18 : PMW_COLORS_NEUTRAL.NEUTRAL_70}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.34204 11.5V14.1667"
                stroke={isPosterZoomOptionSelected() ? PMW_COLORS_NEUTRAL.NEUTRAL_18 : PMW_COLORS_NEUTRAL.NEUTRAL_70}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ClickableDiv>
        ) : null}
      </div>
    </div>
  );
}
